/* eslint-disable max-lines-per-function */
import {fabric} from 'fabric';
import {useContext, useRef, forwardRef, useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import heic2any from 'heic2any';
import {toastAlert} from '~library/atoms/Toast';
import {GCS_PATH_IDS, gcsUploadImage} from '~/common/storage/gcs_storage';
import {charcoal, mexicanOpalWebText} from '~libSass/base/_exports.sass';
import {getEventId} from '~/common/fabric/reducers/eviteEvent/selectors';
import {
  SET_UPLOADED_IMAGE,
  UPDATE_DESIGN_OPTIONS,
} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {
  selectActiveCardEditor,
  selectCountOfUyoImages,
  selectCurrentSection,
  selectFabricArtBackgroundImage,
  selectShouldUseCachedGalleryPhoto,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {useNavigator} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {AddPhotosLineIcon} from '~library/svg/AddPhotosLineIcon';
import {UploadIcon} from '~library/svg/UploadIcon';
import {deriveIdFromCanvasObject} from '~/CreateFabricInvitation/utils/canvas';
import {useCacheGalleryImg} from '~/common/hooks/useCacheGalleryImg';
import {dataURLToFile} from '~/common/utils/dataURLToFile';

import './UploadButton.sass';

export const imageFileUpload = async (file, eventId) => {
  if (file.size > 10 ** 7) {
    toastAlert('Please select an image less than 10MB.', {
      className: 'uyo_toast__error',
      title: 'This image is too large',
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} color={mexicanOpalWebText} />,
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    throw new Error('Please select an image less than 10MB.');
  }

  const gcsUploadArgs = {
    gcsPathId: GCS_PATH_IDS.anonymous_image,
    params: {
      event_id: eventId,
      image_id: '',
    },
    image: file,
  };

  return gcsUploadImage(gcsUploadArgs);
};

let isImgRendered = false;

export const UploadButton = forwardRef(
  (
    {
      section,
      editor,
      className = '',
      styles,
      callback,
      id,
      index = 1,
      btnText = '',
      isInNavBottom = false,
      showFullWidth = false,
    },
    ref
  ) => {
    const {canvas, processing, setProcessing} = useContext(EventCreateContext);
    const eventId = useSelector(getEventId);
    const backgroundImage = useSelector(selectFabricArtBackgroundImage);
    const fileRef = useRef();
    const dispatch = useDispatch();
    const isLargishAndUp = useIsLargishAndUp();
    const isMobile = !isLargishAndUp;
    const navigate = useNavigator({isMobile, disableAutoOpenEditor: true});
    const {galleryImg} = useCacheGalleryImg();
    const countOfUyoImages = useSelector(selectCountOfUyoImages);
    const isMultipleUyo = countOfUyoImages > 1;
    const onClickHandler = useCallback(() => {
      if (isInNavBottom) {
        navigate('card_design');
      } else {
        fileRef.current.click();
      }
    }, [isInNavBottom, navigate]);
    const shouldUseCachedGalleryPhoto = useSelector(selectShouldUseCachedGalleryPhoto);

    const setImgClipPathIfNeeded = (originalObject, img, bgImage) => {
      const isAnimated = /\.(mp4|gif|webm)$/i.test(bgImage.src || '');
      if (originalObject && (isAnimated || isMultipleUyo)) {
        // eslint-disable-next-line no-param-reassign
        img.clipPath = new fabric.Rect({
          left: originalObject.left - img.left,
          top: originalObject.top - img.top,
          width: originalObject.width * originalObject.scaleX,
          height: originalObject.height * originalObject.scaleY,
          originX: 'left',
          originY: 'top',
          absolutePositioned: true,
        });
      }
    };

    const setUYOImage = (img) => {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {activeCardEditor: 'image'},
      });
      const uyoImage = canvas
        ?.getObjects()
        .find((obj) => obj.type === 'uYOImage' && (obj.id || deriveIdFromCanvasObject(obj)) === id);
      // eslint-disable-next-line no-underscore-dangle
      const originalObject = uyoImage?._originalObject || uyoImage?.toJSON();
      if (!originalObject) return;

      const effectiveWidth = originalObject.width * originalObject.scaleX;
      const effectiveHeight = originalObject.height * originalObject.scaleY;
      const scaleX = effectiveWidth / (img?.width || 1);
      const scaleY = effectiveHeight / (img?.height || 1);
      const scale = Math.min(scaleX, scaleY);
      img.scale(scale);
      setImgClipPathIfNeeded(originalObject, img, backgroundImage);

      if (!canvas.getObjects().find((obj) => obj.type === 'rect' && obj.id === id)) {
        const rect = new fabric.Rect({
          left: originalObject.left,
          top: originalObject.top,
          id,
          width: originalObject.width,
          height: originalObject.height,
          scaleX: originalObject.scaleX,
          scaleY: originalObject.scaleY,
          fill: 'white',
          selectable: false,
          hasControls: false,
        });
        canvas.insertAt(rect, 0);
      }

      const centerX = originalObject.left + (originalObject.width * originalObject.scaleX) / 2;
      const centerY = originalObject.top + (originalObject.height * originalObject.scaleY) / 2;

      img.set({
        left: centerX,
        top: centerY,
        originX: 'center',
        originY: 'center',
        centeredRotation: true,
        selectable: true,
        hasControls: true,
        id,
        _originalObject: originalObject,
      });
      canvas.remove(uyoImage);
      canvas.insertAt(img, 1);
      canvas.setActiveObject(img);
      // Guarantee that the 'background' rects stay at the background behind the actual images
      canvas?.getObjects().forEach((obj) => {
        if (obj.type === 'rect') {
          canvas.sendToBack(obj);
        }
      });

      canvas.renderAll();
      canvas.fire('object:added');

      callback?.(img);
    };

    const activeCardEditor = useSelector(selectActiveCardEditor);
    const currentSection = useSelector(selectCurrentSection);

    const activeClass = () => {
      if (editor) {
        return editor === activeCardEditor ? 'create_flow__nav_bottom__section--active' : '';
      }
      return currentSection === section ? 'create_flow__nav_bottom__section--active' : '';
    };

    const activeButtonClass = () => {
      if (editor) {
        return editor === activeCardEditor ? 'active' : '';
      }
      return currentSection === section ? 'active' : '';
    };

    const onChangeImage = async (event) => {
      const files = event.target ? event.target.files : [];
      let [file] = files;

      if (file) {
        try {
          setProcessing(true);

          // since canvas can't render heic files, convert to jpeg
          if (file.type === 'image/heic') {
            const blob = await heic2any({blob: file, toType: 'image/jpeg', quality: 0.9});
            file = new File([blob], 'image.jpeg');
          }

          const {url, gcsPath, imageKey} = await imageFileUpload(file, eventId);
          fabric.UYOImage.fromURL(url, setUYOImage);

          dispatch({
            type: SET_UPLOADED_IMAGE,
            payload: {url, gcsPath, imageKey},
          });
          dispatch({
            type: UPDATE_DESIGN_OPTIONS,
            payload: {shouldUseCachedGalleryPhoto: false},
          });
        } finally {
          setProcessing(false);
        }
      }
    };

    useEffect(() => {
      async function uploadCachedImgToCloud() {
        try {
          setProcessing(true);
          const {url, gcsPath, imageKey} = await imageFileUpload(
            dataURLToFile(galleryImg, 'anonymous_image'),
            eventId
          );
          fabric.UYOImage.fromURL(url, setUYOImage);

          dispatch({
            type: SET_UPLOADED_IMAGE,
            payload: {url, gcsPath, imageKey},
          });
        } finally {
          setProcessing(false);
        }
      }

      if (galleryImg?.length > 0 && shouldUseCachedGalleryPhoto && !isImgRendered) {
        // TODO: Move isImgRendered state change into a then() statement on uploadCachedImgToCloud()
        uploadCachedImgToCloud();
        isImgRendered = true;
      }
    }, [galleryImg, shouldUseCachedGalleryPhoto]);

    if (isInNavBottom) {
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus
        <div
          className={`create_flow__nav_bottom__section ${activeClass()}`}
          role="button"
          onClick={onClickHandler}
          data-qa-id="nav-bottom-photo-upload"
          ref={ref}
        >
          <input
            ref={fileRef}
            type="file"
            id="uyo-footer-upload-input"
            accept="image/png,image/jpeg"
            onChange={onChangeImage}
            hidden
          />
          <div className="create_flow__nav_bottom__section__button-wrapper">
            <Button
              id="navBottom"
              variant="secondary"
              size="small"
              className={`create_flow__nav_bottom__section__button ${activeButtonClass()}`}
              data-qa-id="add-photos-upload-button"
            >
              <AddPhotosLineIcon color={charcoal} ratio={0.7} />
            </Button>
          </div>
          <p className="create_flow__nav_bottom__section__title">Photo Upload</p>
        </div>
      );
    }

    // Can't use ECL Button component as we (correctly) don't allow the style prop, but is needed here for Canvas positioning
    return (
      <button
        id={`uyoBtn--${index}`}
        className={`button secondary large uyo-upload-button ${btnText ? 'hasText' : ''} ${showFullWidth ? 'wide' : ''} ${className}`}
        onClick={onClickHandler}
        data-qa-id={`photo-editor_upload-button--${index}`}
        type="button"
        style={styles}
        ref={ref}
      >
        <input
          ref={fileRef}
          type="file"
          id={`uyo-upload-input--card-${index}`}
          accept="image/png,image/jpeg,image/jpg,image/heic"
          onChange={onChangeImage}
          hidden
        />
        {processing ? (
          <span className="evt-spinner inline no-margin" />
        ) : (
          <>
            <UploadIcon className="action_button__upload" color={charcoal} ratio={0.5} />
            {btnText && (
              <Typography
                classNames="uyo-upload-button__text"
                variant="paragraph3"
                size="medium"
                color="charcoal"
              >
                {btnText}
              </Typography>
            )}
          </>
        )}
      </button>
    );
  }
);
